// Platform abstraction.
//
// Same React code ships to two targets:
//   - Tauri webview (desktop): system-browser SSO, deeplink callback,
//     stronghold for secrets.
//   - Static web SPA (chat.nurion.com): same-origin SSO, query-string
//     callback, IndexedDB for everything.
//
// All target-specific calls go through this module. Components must not
// import from `@tauri-apps/*` directly.

import type { ThemeChoice, ThemeStore } from "@nurion/theme/react";

import { getLoadedConfig } from "~/config";
import { envBuildTarget } from "~/env";
import { LOCAL_SEARCH_DB_PREFIX } from "~/lib/local-search-index";

export type Platform = "tauri" | "web";

export type MobileConnectionState = "online" | "connecting" | "offline";

export interface MobileLifecycleSnapshot {
  visibility: "foreground" | "background";
  reachability: "online" | "offline";
  connection: MobileConnectionState;
  badgeCount: number;
  reconnectAttempt: number;
  reconnectBackoffMs: number;
}

export interface LaunchNotification {
  roomId: string;
}

export interface SecretsError {
  kind: "keychainUnavailable" | "keychainTransient" | "keychainNotFound" | "io" | "entropy";
  message?: string;
}

// Ceiling on the snapshot-key keychain fetch. A first launch whose
// keychain access stalls (macOS ACL authorization not yet granted)
// must not hang the boot pipeline on a blank "Connecting…" screen —
// past this bound we surface a recoverable transient error instead.
export const SNAPSHOT_KEY_TIMEOUT_MS = 30_000;

interface SnapshotKeyResult {
  password: string;
  legacyDetected: boolean;
}

let snapshotKeyCache: SnapshotKeyResult | null = null;

/**
 * The build target this bundle was compiled for. Determined by Vite's
 * compile-time `MODE` constant — see `~/env.ts`. The platform branch
 * is a build-time concern, never runtime, so it stays bake-able into
 * the embed bundle even after #2553 decoupled the runtime env values.
 */
export const platform: Platform = envBuildTarget;
const mobileNativeLifecycle =
  platform === "tauri" && import.meta.env.VITE_BUILD_TARGET === "mobile";

/** The iOS/Android app, where the camera makes QR the natural verification. */
export const isMobileApp = mobileNativeLifecycle;

// Stronghold is the desktop-only secret backend. The mobile native build
// keeps its session + sqlite passphrase in the host's Keychain/Keystore
// (consumed Rust-side, no webview surface), so the webview's own secret
// store routes to the IndexedDB fallback rather than the unregistered
// stronghold plugin.
function isStrongholdTarget(): boolean {
  return platform === "tauri" && !mobileNativeLifecycle;
}

export function isMacHost(platformLabel?: string): boolean {
  if (platformLabel) return /^(macOS|Mac|MacIntel|iPhone|iPad|iPod)$/i.test(platformLabel);
  if (typeof navigator === "undefined") return false;
  const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
  if (uaData?.platform) return uaData.platform === "macOS";
  return /\b(Mac\w*|iPhone|iPad|iPod)\b/i.test(navigator.platform || navigator.userAgent || "");
}

export function shortcutHintsEnabled(): boolean {
  if (isMobileApp) return false;
  if (isTouchDeviceUserAgent()) return false;
  if (!hasFinePointer()) return false;
  return true;
}

function isTouchDeviceUserAgent(): boolean {
  if (typeof navigator === "undefined") return false;
  const uaData = (navigator as { userAgentData?: { platform?: string } }).userAgentData;
  const haystack = [navigator.userAgent, navigator.platform, uaData?.platform]
    .filter(Boolean)
    .join(" ");
  if (/(Android|iPhone|iPad|iPod)/i.test(haystack)) return true;
  return navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1;
}

function hasFinePointer(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  const coarse = window.matchMedia("(pointer: coarse)").matches;
  const anyFine = window.matchMedia("(any-pointer: fine)").matches;
  return !(coarse && !anyFine);
}

// Matches the cookie name so the FOUC script in index.html reads the same value.
const THEME_STORAGE_KEY = "nurion-theme";

// The macOS `tauri://` WKWebView scheme drops `document.cookie`, so the desktop
// build persists the theme choice in localStorage instead.
export const localStorageThemeStore: ThemeStore = {
  read(): ThemeChoice {
    if (typeof localStorage === "undefined") return null;
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    return value === "dark" || value === "light" ? value : null;
  },
  write(value: ThemeChoice): void {
    if (typeof localStorage === "undefined") return;
    if (value === null) localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, value);
  },
};

// localStorage store for Tauri; `null` keeps the shared cookie default on web.
export function getThemeStore(): ThemeStore | null {
  return platform === "tauri" ? localStorageThemeStore : null;
}

/** Stable keys the app persists. Exposed so callers can enumerate them
 *  when wiping on logout. */
export const SECRET_KEYS = [
  "matrix.session",
  "matrix.displayName",
  // Pending OIDC store name. Set at `startOidcLogin` (which mints the
  // wasm client's IndexedDB store), cleared after `finishOidcLogin`
  // commits the durable session.
  "matrix.oidc.pending-store",
  // Serialized in-flight OAuth authorization data (PKCE verifier,
  // server metadata, etc.) + client_id. Persisted at `startOidcLogin`
  // so a same-tab redirect can restore the SDK's internal state on the
  // callback page and complete the token exchange.
  "matrix.oidc.pending-inflight",
] as const;
export type SecretKey = (typeof SECRET_KEYS)[number];

export const CHAT_SECRET_DB_NAME = "nurion-chat-secrets";
export const CHAT_TIMELINE_CACHE_DB_NAME = "nurion-chat-timeline-cache";
export const MATRIX_SESSION_STORE_PREFIX = "nurion-chat-matrix-mas";

const TIMELINE_CACHE_STORE = "rooms";
const TIMELINE_CACHE_DB_VERSION = 1;
export const TIMELINE_CACHE_EVENT_LIMIT = 50;
export const TIMELINE_CACHE_ROOM_LIMIT = 20;

export interface TimelineCacheRow<TEvent> {
  roomId: string;
  events: TEvent[];
  updatedAt: number;
}

/** Open a URL in the system browser (Tauri) or a new tab (web).
 *
 *  On web we deliberately do NOT replace the current location — the org
 *  rail's "+" opens Console in a new tab so the chat SPA stays mounted
 *  (we'd lose any in-flight Matrix sync otherwise).
 */
export async function openExternal(url: string): Promise<void> {
  if (platform === "tauri") {
    const { openUrl } = await import("@tauri-apps/plugin-opener");
    await openUrl(url);
    return;
  }
  // `noopener,noreferrer` prevents the new page from accessing
  // `window.opener` and leaking the referrer.
  window.open(url, "_blank", "noopener,noreferrer");
}

/** Handlers for native OS file drag-drop over the desktop window. */
export interface FileDropListener {
  /** Files are hovering the window (drag enter / over). */
  onEnter?: () => void;
  /** The drag left the window or was cancelled. */
  onLeave?: () => void;
  /** Files were dropped; receives their absolute OS paths. */
  onDrop: (paths: readonly string[]) => void;
}

/**
 * Subscribe to Tauri's native OS file drag-drop on the current webview.
 *
 * Desktop webviews default to `dragDropEnabled: true`, which makes the
 * OS deliver file drags to the Tauri layer *before* the DOM — so the
 * HTML5 `DropZone` handlers never fire on the desktop build (#7755).
 * This bridges the native event to the same drop pipeline: `onEnter`/
 * `onLeave` drive the overlay, `onDrop` yields the file paths to read
 * via {@link readDroppedFile}.
 *
 * No-op on web (returns a noop unlisten) — the web build keeps the
 * HTML5 `dataTransfer` path on the `DropZone` element.
 */
export async function onFileDrop(listener: FileDropListener): Promise<() => void> {
  if (platform !== "tauri") return () => {};
  const { getCurrentWebview } = await import("@tauri-apps/api/webview");
  return getCurrentWebview().onDragDropEvent((event) => {
    const payload = event.payload;
    if (payload.type === "enter" || payload.type === "over") listener.onEnter?.();
    else if (payload.type === "leave") listener.onLeave?.();
    else if (payload.type === "drop") listener.onDrop(payload.paths);
  });
}

// Common OS-file extensions → MIME type. Native drag-drop hands us a
// path, not a `File`, so we infer the type the file picker would have
// set — `useFileUpload` routes images/videos on the MIME prefix.
// Unknown extensions fall through to the upload path's
// `application/octet-stream` default.
const DROPPED_FILE_MIME_BY_EXT: Record<string, string> = {
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  webp: "image/webp",
  avif: "image/avif",
  svg: "image/svg+xml",
  heic: "image/heic",
  bmp: "image/bmp",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
  pdf: "application/pdf",
};

/** Basename of an OS path (handles both `/` and `\` separators). */
export function droppedFileName(path: string): string {
  const segments = path.split(/[\\/]/);
  return segments[segments.length - 1] || "file";
}

/** Best-effort MIME type for a dropped file name; `""` when unknown. */
export function droppedFileMimeType(name: string): string {
  const dot = name.lastIndexOf(".");
  if (dot < 0) return "";
  return DROPPED_FILE_MIME_BY_EXT[name.slice(dot + 1).toLowerCase()] ?? "";
}

/**
 * Read an OS-dropped file's bytes (via the Rust `read_dropped_file`
 * command) and wrap them in a `File` the upload pipeline consumes —
 * the native drag-drop event only hands us a path. Tauri-only; the web
 * build receives real `File`s through HTML5 `dataTransfer`.
 */
export async function readDroppedFile(path: string): Promise<File> {
  const { invoke } = await import("@tauri-apps/api/core");
  const bytes = await invoke<ArrayBuffer>("read_dropped_file", { path });
  const name = droppedFileName(path);
  return new File([bytes], name, { type: droppedFileMimeType(name) });
}

// The popup approach (openOidcPopup + postMessage) was replaced by
// same-tab redirect now that the SDK fork supports persisting in-flight
// OAuth authorization data (PKCE verifier, server metadata). See #3267.

/** Restart the desktop app. No-op on web — the updater pipeline is
 *  Tauri-only, and the web build never invokes this. */
export async function relaunchApp(): Promise<void> {
  if (platform !== "tauri") return;
  const { relaunch } = await import("@tauri-apps/plugin-process");
  await relaunch();
}

/** Install only the release explicitly accepted in the update prompt, then restart. */
export async function installUpdateAndRelaunch(version: string): Promise<void> {
  if (platform !== "tauri") return;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("install_app_update", { version });
  await relaunchApp();
}

/** Persist a version string the user has explicitly chosen to skip
 *  (#2167). Calls the Rust-side `skip_update_version` command, which
 *  writes the version into `app_local_data_dir()/updater-state.json`
 *  and clears the debounce timestamp so the next launch can pick up
 *  a *newer* version immediately.
 *
 *  No-op on web — the updater pipeline is Tauri-only, and the web
 *  build never surfaces the toast that would invoke this. */
export async function skipUpdateVersion(version: string): Promise<void> {
  if (platform !== "tauri") return;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("skip_update_version", { version });
}

/** Subscribe before starting the background check so the update offer cannot
 *  be emitted before the frontend listener is ready. No download or installation
 *  occurs until the user accepts the offer.
 *
 *  Returns an unlisten function; a no-op on web. */
export async function onUpdateReady(handler: (version: string) => void): Promise<() => void> {
  if (platform !== "tauri") return () => {};
  const { listen } = await import("@tauri-apps/api/event");
  const unlisten = await listen<{ version: string }>("update-ready", (event) => {
    const version = event.payload?.version;
    if (typeof version === "string" && version.length > 0) {
      handler(version);
    }
  });
  const { invoke } = await import("@tauri-apps/api/core");
  void invoke("check_app_update").catch((err) => console.error("Update check failed", err));
  return unlisten;
}

function isMobileConnectionState(value: unknown): value is MobileConnectionState {
  return value === "online" || value === "connecting" || value === "offline";
}

function normaliseLifecycleSnapshot(raw: unknown): MobileLifecycleSnapshot | null {
  if (!raw || typeof raw !== "object") return null;
  const input = raw as Partial<MobileLifecycleSnapshot>;
  if (input.visibility !== "foreground" && input.visibility !== "background") return null;
  if (input.reachability !== "online" && input.reachability !== "offline") return null;
  if (!isMobileConnectionState(input.connection)) return null;
  return {
    visibility: input.visibility,
    reachability: input.reachability,
    connection: input.connection,
    badgeCount: typeof input.badgeCount === "number" ? input.badgeCount : 0,
    reconnectAttempt: typeof input.reconnectAttempt === "number" ? input.reconnectAttempt : 0,
    reconnectBackoffMs: typeof input.reconnectBackoffMs === "number" ? input.reconnectBackoffMs : 0,
  };
}

function normaliseLaunchNotification(raw: unknown): LaunchNotification | null {
  if (!raw || typeof raw !== "object") return null;
  const roomId = (raw as { roomId?: unknown }).roomId;
  return typeof roomId === "string" && roomId.length > 0 ? { roomId } : null;
}

async function applyMobileOsBadge(total: number): Promise<void> {
  if (!mobileNativeLifecycle) return;
  try {
    const { setBadgeCount } = (await import("@tauri-apps/api/app")) as {
      setBadgeCount?: (count?: number) => Promise<void>;
    };
    if (typeof setBadgeCount === "function") {
      await setBadgeCount(total > 0 ? total : undefined);
    }
  } catch {
    return;
  }
}

export async function mobileLifecycleSnapshot(): Promise<MobileLifecycleSnapshot | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  return normaliseLifecycleSnapshot(await invoke("matrix_lifecycle_snapshot"));
}

export async function onMobileLifecycle(
  handler: (snapshot: MobileLifecycleSnapshot) => void,
): Promise<() => void> {
  if (!mobileNativeLifecycle) return () => {};
  const { listen } = await import("@tauri-apps/api/event");
  const unlisten = await listen<unknown>("mobile-lifecycle", (event) => {
    const snapshot = normaliseLifecycleSnapshot(event.payload);
    if (snapshot) handler(snapshot);
  });
  return unlisten;
}

export async function markMobileLifecycleConnected(): Promise<void> {
  if (!mobileNativeLifecycle) return;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("matrix_lifecycle_connected");
}

export async function markMobileLifecycleBackground(
  unreadCount: number,
): Promise<MobileLifecycleSnapshot | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  const snapshot = normaliseLifecycleSnapshot(
    await invoke("matrix_lifecycle_background", { unreadCount: Math.max(0, unreadCount) }),
  );
  await applyMobileOsBadge(snapshot?.badgeCount ?? 0);
  return snapshot;
}

export async function markMobileLifecycleForeground(): Promise<MobileLifecycleSnapshot | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  // The icon badge follows the live unread count (`setMobileAppIconBadge`);
  // coming to the foreground must not zero it while rooms are still unread.
  return normaliseLifecycleSnapshot(await invoke("matrix_lifecycle_foreground"));
}

export async function setMobileReachability(
  online: boolean,
): Promise<MobileLifecycleSnapshot | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  return normaliseLifecycleSnapshot(await invoke("matrix_lifecycle_reachability", { online }));
}

export async function clearMobileBadge(): Promise<void> {
  if (!mobileNativeLifecycle) return;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("matrix_lifecycle_clear_badge");
}

/** Set the app icon badge to the number of unread messages; 0 removes it. */
export async function setMobileAppIconBadge(total: number): Promise<void> {
  await applyMobileOsBadge(Math.max(0, Math.floor(total)));
}

export async function getLaunchNotification(): Promise<LaunchNotification | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  return normaliseLaunchNotification(await invoke("matrix_get_launch_notification"));
}

/** Drain a cold-start OIDC callback the host buffered when the OS
 *  launched the app via the `com.nurion.chat:/oidc/callback` deep link
 *  before the webview attached its `oidc-callback` listener. Returns the
 *  raw query string once, then null. No-op off the mobile native build. */
export async function takeLaunchOidcCallback(): Promise<string | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  const query = await invoke("matrix_get_launch_oidc_callback");
  return typeof query === "string" && query.length > 0 ? query : null;
}

export async function onNotificationTap(
  handler: (notification: LaunchNotification) => void,
): Promise<() => void> {
  if (!mobileNativeLifecycle) return () => {};
  const { listen } = await import("@tauri-apps/api/event");
  const unlisten = await listen<unknown>("mobile-notification-tap", (event) => {
    const notification = normaliseLaunchNotification(event.payload);
    if (notification) handler(notification);
  });
  return unlisten;
}

/** Run the MAS authorize `url` in the native in-app sign-in sheet (iOS),
 *  which delivers the callback via the `oidc-callback` event. False when the
 *  host has no in-app session, so the caller opens the browser instead. */
export async function authenticateInApp(url: string): Promise<boolean> {
  if (!mobileNativeLifecycle) return false;
  const { invoke } = await import("@tauri-apps/api/core");
  return (await invoke<boolean>("oidc_authenticate_in_app", { url })) === true;
}

/** Prompt for push permission and register for APNs, returning the device
 *  token base64-encoded as Sygnal's pushkey. Null when permission is denied or
 *  off the mobile native build (incl. Android, which has no APNs). */
export async function registerApnsToken(): Promise<string | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  const token = await invoke<string | null>("push_register_apns");
  return typeof token === "string" && token.length > 0 ? token : null;
}

/** Drain the room id of a notification tap the native layer buffered (cold
 *  start or foreground). Null when nothing is buffered or off the native
 *  build. */
export async function takeLaunchPushTap(): Promise<string | null> {
  if (!mobileNativeLifecycle) return null;
  const { invoke } = await import("@tauri-apps/api/core");
  const roomId = await invoke<string | null>("push_take_launch_tap");
  return typeof roomId === "string" && roomId.length > 0 ? roomId : null;
}

export type AndroidPushRegistrationOutcome =
  | "registered"
  | "denied"
  | "noDistributor"
  | "network"
  | "unsupported";

export interface AndroidPushEndpoint {
  instance: string;
  endpoint: string;
}

function normaliseRegistrationOutcome(raw: unknown): AndroidPushRegistrationOutcome {
  if (
    raw === "registered" ||
    raw === "denied" ||
    raw === "noDistributor" ||
    raw === "network" ||
    raw === "unsupported"
  ) {
    return raw;
  }
  return "unsupported";
}

export async function requestAndroidPushRegistration(): Promise<AndroidPushRegistrationOutcome> {
  if (!mobileNativeLifecycle) return "unsupported";
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    return normaliseRegistrationOutcome(await invoke("android_push_register"));
  } catch {
    return "unsupported";
  }
}

export async function unregisterAndroidPush(instance?: string): Promise<void> {
  if (!mobileNativeLifecycle) return;
  await import("@tauri-apps/api/core")
    .then((m) => m.invoke("android_push_unregister", { instance: instance ?? "" }))
    .catch(() => undefined);
}

export async function takeAndroidPushTap(): Promise<string | null> {
  if (!mobileNativeLifecycle) return null;
  try {
    const { invoke } = await import("@tauri-apps/api/core");
    const roomId = await invoke<string | null>("android_push_take_tap");
    return typeof roomId === "string" && roomId.length > 0 ? roomId : null;
  } catch {
    return null;
  }
}

export async function onAndroidPushEndpoint(
  handler: (endpoint: AndroidPushEndpoint) => void,
): Promise<() => void> {
  if (!mobileNativeLifecycle) return () => {};
  const { listen } = await import("@tauri-apps/api/event");
  const unlisten = await listen<unknown>("android-push-endpoint", (event) => {
    const payload = event.payload as Partial<AndroidPushEndpoint>;
    if (
      payload &&
      typeof payload.instance === "string" &&
      typeof payload.endpoint === "string" &&
      payload.instance.length > 0 &&
      payload.endpoint.length > 0
    ) {
      handler({ instance: payload.instance, endpoint: payload.endpoint });
    }
  });
  return unlisten;
}

/** The redirect URL MAS should return the OIDC `code`/`state` to. */
export function getCallbackUrl(): string {
  if (platform === "tauri") {
    return "com.nurion.chat:/oidc/callback";
  }
  return `${window.location.origin}/oidc/callback`;
}

/**
 * Public URL of the chat *client application* — surfaced on the MAS
 * consent screen as the "Client URI" link the user clicks to learn
 * what they are granting access to. Per the OAuth 2.0 Dynamic Client
 * Registration spec (RFC 7591 §2), `client_uri` is the URL of the
 * client app, NOT the resource server / homeserver. (#3186)
 *
 * Resolution order (web target):
 *
 *   1. Runtime config (`/config.json` → `chatPublicUrl`) — each
 *      deployment mounts its own config so staging shows its own URL
 *      on the MAS consent screen and registers a distinct MAS
 *      dynamic-client-registration record from prod (#3192). MAS
 *      keys client identity off `(client_uri, application_type)`, so a
 *      stable per-env value avoids prod/staging sharing one record.
 *   2. PROD `window.location.origin` — used when no config override
 *      is set. Non-`https://` origins downgrade to the production
 *      constant and warn so MAS never sees an unusable scheme.
 *   3. Production constant — covers Storybook / jsdom / SSR / dev
 *      where neither a config override nor a real origin exists. MAS
 *      rejects `http://localhost` and similar, so the consent screen
 *      always shows a real, resolvable host.
 *
 * Tauri target: there is no meaningful "origin" for a desktop app
 * loaded over `tauri://` and there is no per-env Tauri build today.
 * Always returns the production constant.
 *
 * Trailing slash is dropped; MAS normalises but a canonical value
 * avoids spurious dynamic-client-registration churn on round-trips.
 */
export const CHAT_PRODUCTION_PUBLIC_URL = "https://chat.nurion.com";

function stripTrailingSlash(value: string): string {
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

export function getChatPublicUrl(): string {
  if (platform === "tauri") {
    return CHAT_PRODUCTION_PUBLIC_URL;
  }
  // Runtime config override — /config.json is the primary source for
  // web deployments. Falls back to the build-time env var for embed /
  // dev / test contexts that don't call loadConfig().
  const cfg = getLoadedConfig();
  const rawOverride = cfg?.chatPublicUrl || import.meta.env.VITE_CHAT_PUBLIC_URL;
  if (typeof rawOverride === "string" && rawOverride.length > 0) {
    const override = stripTrailingSlash(rawOverride);
    if (!override.startsWith("https://")) {
      // Never hand MAS a non-`https://` `client_uri`. A misconfigured
      // deployment recovers without breaking sign-in.
      console.warn(
        `[chat] getChatPublicUrl: non-HTTPS chatPublicUrl '${rawOverride}' — falling back to ${CHAT_PRODUCTION_PUBLIC_URL}`,
      );
      return CHAT_PRODUCTION_PUBLIC_URL;
    }
    return override;
  }
  // `import.meta.env.PROD` is folded into a constant by Vite at build
  // time. In dev / preview / Storybook it's `false` and we fall back
  // to the production URL so the consent screen always shows a real,
  // resolvable host.
  if (!import.meta.env.PROD) {
    return CHAT_PRODUCTION_PUBLIC_URL;
  }
  if (typeof window === "undefined") {
    return CHAT_PRODUCTION_PUBLIC_URL;
  }
  // PROD bundles MUST hand MAS an `https://` origin; a misconfigured
  // CDN / edge proxy serving over `http://` would otherwise produce a
  // value MAS rejects with an opaque error. Fall back to the constant
  // and log a warning so the operator sees the misconfig in the
  // console without breaking sign-in.
  const origin = window.location.origin;
  if (!origin.startsWith("https://")) {
    console.warn(
      `[chat] getChatPublicUrl: non-HTTPS origin '${origin}' — falling back to ${CHAT_PRODUCTION_PUBLIC_URL}`,
    );
    return CHAT_PRODUCTION_PUBLIC_URL;
  }
  return origin;
}

/**
 * Persist a secret. On Tauri we use the stronghold plugin (encrypted, on
 * disk via IronOxide). On web we fall back to a namespaced IndexedDB key —
 * not as strong, but the only option without a native runtime.
 */
export async function storeSecret(key: SecretKey, value: string): Promise<void> {
  if (isStrongholdTarget()) {
    const store = await strongholdStore();
    await store.insert(key, Array.from(new TextEncoder().encode(value)));
    await store.save();
    return;
  }
  await idbPut(key, value);
}

/** Read a previously-stored secret, or null if absent. */
export async function readSecret(key: SecretKey): Promise<string | null> {
  if (isStrongholdTarget()) {
    const store = await strongholdStore();
    const bytes = await store.get(key);
    if (!bytes) return null;
    return new TextDecoder().decode(bytes);
  }
  return idbGet(key);
}

/** Wipe all locally-stored secrets. Called on logout. */
export async function wipeSecrets(): Promise<void> {
  if (isStrongholdTarget()) {
    try {
      const store = await strongholdStore();
      // Remove each known key. The plugin exposes `remove` per-key; we
      // prefer that over a blanket snapshot unload (which wouldn't
      // actually delete data from disk).
      for (const key of SECRET_KEYS) {
        await store.remove(key).catch(() => undefined);
      }
      await store.save();
    } catch (err) {
      // If stronghold fails to open (wrong password, corrupt file) there
      // is nothing usable to clear — best-effort only.
      console.warn("wipeSecrets: stronghold open failed", err);
    }
    const { invoke } = await import("@tauri-apps/api/core");
    await invoke("secrets_wipe");
    snapshotKeyCache = null;
    strongholdStorePromise = null;
    return;
  }
  await idbClear();
}

export async function wipeDesktopSecrets(): Promise<void> {
  if (!isStrongholdTarget()) return;
  const { invoke } = await import("@tauri-apps/api/core");
  await invoke("secrets_wipe");
  snapshotKeyCache = null;
  strongholdStorePromise = null;
}

export async function detectLegacySnapshot(): Promise<boolean> {
  if (!isStrongholdTarget()) return false;
  if (!snapshotKeyCache) await strongholdPassword();
  return snapshotKeyCache?.legacyDetected ?? false;
}

export async function refreshStrongholdSnapshotKey(): Promise<void> {
  if (!isStrongholdTarget()) return;
  snapshotKeyCache = null;
  strongholdStorePromise = null;
  await strongholdPassword();
}

export function isKeychainUnavailableError(err: unknown): err is SecretsError {
  if (typeof err !== "object" || err === null || !("kind" in err)) return false;
  const kind = (err as { kind?: unknown }).kind;
  return kind === "keychainUnavailable";
}

export function isKeychainTransientError(err: unknown): err is SecretsError {
  if (typeof err !== "object" || err === null || !("kind" in err)) return false;
  const kind = (err as { kind?: unknown }).kind;
  return kind === "keychainTransient";
}

/** Firefox lacks `indexedDB.databases()`, so prefix-only stranded wasm stores cannot be enumerated there. */
export async function deleteChatIndexedDbDatabases(
  storePrefix?: string | null,
  concreteStoreName?: string | null,
): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const databases = (indexedDB as unknown as { databases?: () => Promise<IDBDatabaseInfo[]> })
    .databases;
  const fallbackNames = () => chatIndexedDbDeleteNames(storePrefix, false, concreteStoreName);
  if (typeof databases !== "function") {
    await Promise.all([...fallbackNames()].map(deleteIndexedDbDatabase));
    return;
  }
  try {
    const dbs = await databases.call(indexedDB);
    const names = chatIndexedDbDeleteNames(storePrefix, true, concreteStoreName);
    for (const db of dbs) {
      if (!db.name) continue;
      if (storePrefix && db.name.startsWith(storePrefix)) names.add(db.name);
      if (db.name.startsWith(LOCAL_SEARCH_DB_PREFIX)) names.add(db.name);
    }
    await Promise.all([...names].map(deleteIndexedDbDatabase));
  } catch {
    await Promise.all([...fallbackNames()].map(deleteIndexedDbDatabase));
  }
}

function chatIndexedDbDeleteNames(
  storePrefix: string | null | undefined,
  allowPrefixOnly: boolean,
  concreteStoreName?: string | null,
) {
  const names = new Set<string>([CHAT_SECRET_DB_NAME, CHAT_TIMELINE_CACHE_DB_NAME]);
  if (concreteStoreName) addMatrixStoreDeleteNames(names, concreteStoreName);
  if (!storePrefix || (!allowPrefixOnly && storePrefix.endsWith("-"))) return names;
  addMatrixStoreDeleteNames(names, storePrefix);
  return names;
}

function addMatrixStoreDeleteNames(names: Set<string>, storeName: string): void {
  names.add(storeName);
  names.add(`${storeName}::matrix-sdk-state`);
  names.add(`${storeName}::matrix-sdk-crypto`);
  names.add(`${storeName}::matrix-sdk-crypto-meta`);
}

export async function readTimelineCache<TEvent>(roomId: string): Promise<TEvent[]> {
  try {
    const row = await withTimelineCacheStore<TimelineCacheRow<TEvent> | undefined>(
      "readonly",
      (store) => store.get(roomId),
    );
    if (!row || !Array.isArray(row.events)) return [];
    return row.events;
  } catch {
    return [];
  }
}

export async function writeTimelineCache<TEvent>(roomId: string, events: TEvent[]): Promise<void> {
  try {
    const snapshot = events.slice(-TIMELINE_CACHE_EVENT_LIMIT);
    await withTimelineCacheStore("readwrite", (store) => {
      store.put({ roomId, events: snapshot, updatedAt: Date.now() });
    });
    await pruneTimelineCacheRooms();
  } catch {
    // Timeline cache is an optimisation; network fetch remains authoritative.
  }
}

export async function clearTimelineCache(): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  await deleteIndexedDbDatabase(CHAT_TIMELINE_CACHE_DB_NAME);
}

/* ── Tauri stronghold helpers ───────────────────────────────────── */

interface StrongholdStore {
  insert(key: string, value: number[]): Promise<unknown>;
  get(key: string): Promise<Uint8Array | null>;
  remove(key: string): Promise<unknown>;
  save(): Promise<void>;
}

let strongholdStorePromise: Promise<StrongholdStore> | null = null;

function strongholdStore(): Promise<StrongholdStore> {
  if (!strongholdStorePromise) {
    const pending = openStrongholdStore().catch((error: unknown) => {
      if (strongholdStorePromise === pending) strongholdStorePromise = null;
      throw error;
    });
    strongholdStorePromise = pending;
  }
  return strongholdStorePromise;
}

async function openStrongholdStore(): Promise<StrongholdStore> {
  const [{ Stronghold }, path] = await Promise.all([
    import("@tauri-apps/plugin-stronghold"),
    import("@tauri-apps/api/path"),
  ]);
  // Resolve the snapshot path under the platform-specific local data
  // dir. A bare relative path would be CWD-dependent at launch — which
  // varies by launcher (Finder, terminal, shortcut) and breaks session
  // persistence. The stronghold plugin (registered on the Rust side)
  // uses the same location for its salt.
  const baseDir = await path.appLocalDataDir();
  const snapshotPath = await path.join(baseDir, "nurion-chat.stronghold.v2");
  const hold = await Stronghold.load(snapshotPath, await strongholdPassword());
  const client = await hold
    .loadClient(strongholdClient())
    .catch(() => hold.createClient(strongholdClient()));
  const store = client.getStore();
  return {
    insert: (key, value) => store.insert(key, value),
    get: async (key) => {
      const bytes = await store.get(key);
      return bytes ? new Uint8Array(bytes) : null;
    },
    remove: (key) => store.remove(key),
    save: () => hold.save(),
  };
}

export async function strongholdPassword(): Promise<string> {
  if (platform !== "tauri") {
    throw new Error("strongholdPassword called on non-Tauri platform");
  }
  if (snapshotKeyCache) return snapshotKeyCache.password;
  const { invoke } = await import("@tauri-apps/api/core");
  const result = await withSnapshotKeyTimeout(
    invoke<SnapshotKeyResult>("secrets_get_snapshot_key"),
  );
  snapshotKeyCache = result;
  return result.password;
}

function withSnapshotKeyTimeout(pending: Promise<SnapshotKeyResult>): Promise<SnapshotKeyResult> {
  return new Promise<SnapshotKeyResult>((resolve, reject) => {
    const timer = setTimeout(() => {
      const error: SecretsError = {
        kind: "keychainTransient",
        message: `keychain access timed out after ${SNAPSHOT_KEY_TIMEOUT_MS}ms`,
      };
      reject(error);
    }, SNAPSHOT_KEY_TIMEOUT_MS);
    pending.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err: unknown) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function strongholdClient(): string {
  return "nurion-chat";
}

/* ── IndexedDB fallback for web ─────────────────────────────────── */

const IDB_NAME = CHAT_SECRET_DB_NAME;
const IDB_STORE = "kv";

function deleteIndexedDbDatabase(name: string): Promise<void> {
  return new Promise((resolve) => {
    const req = indexedDB.deleteDatabase(name);
    req.onsuccess = () => resolve();
    req.onerror = () => resolve();
    req.onblocked = () => resolve();
  });
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(IDB_STORE);
    };
    req.onsuccess = () => {
      if (!req.result.objectStoreNames.contains(IDB_STORE)) {
        req.result.close();
        const deleteReq = indexedDB.deleteDatabase(IDB_NAME);
        deleteReq.onsuccess = () => resolve(openDb());
        deleteReq.onerror = () => reject(deleteReq.error);
        deleteReq.onblocked = () => reject(new Error(`${IDB_NAME} reset blocked`));
        return;
      }
      resolve(req.result);
    };
    req.onerror = () => reject(req.error);
  });
}

function openTimelineCacheDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(CHAT_TIMELINE_CACHE_DB_NAME, TIMELINE_CACHE_DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(TIMELINE_CACHE_STORE)) {
        db.createObjectStore(TIMELINE_CACHE_STORE, { keyPath: "roomId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withTimelineCacheStore<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T> | void,
): Promise<T | undefined> {
  const db = await openTimelineCacheDb();
  return await new Promise<T | undefined>((resolve, reject) => {
    const tx = db.transaction(TIMELINE_CACHE_STORE, mode);
    const store = tx.objectStore(TIMELINE_CACHE_STORE);
    let value: T | undefined;
    const request = run(store);
    if (request) {
      request.onsuccess = () => {
        value = request.result;
      };
    }
    tx.oncomplete = () => {
      db.close();
      resolve(value);
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error ?? new Error("timeline cache tx aborted"));
    };
  });
}

async function pruneTimelineCacheRooms(): Promise<void> {
  const rows =
    (await withTimelineCacheStore<TimelineCacheRow<unknown>[]>(
      "readonly",
      (store) => store.getAll() as IDBRequest<TimelineCacheRow<unknown>[]>,
    )) ?? [];
  if (rows.length <= TIMELINE_CACHE_ROOM_LIMIT) return;
  const staleRoomIds = rows
    .slice()
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(TIMELINE_CACHE_ROOM_LIMIT)
    .map((row) => row.roomId);
  await withTimelineCacheStore("readwrite", (store) => {
    for (const roomId of staleRoomIds) store.delete(roomId);
  });
}

async function idbPut(key: string, value: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(value, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

async function idbGet(key: string): Promise<string | null> {
  const db = await openDb();
  const value = await new Promise<string | null>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(key);
    req.onsuccess = () => resolve(typeof req.result === "string" ? req.result : null);
    req.onerror = () => reject(req.error);
  });
  db.close();
  return value;
}

async function idbClear(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
